import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

const AUTOMATISMES = [
  {
    cle: "vehicule_pret",
    titre: "Véhicule prêt",
    aide: "Prévient le client sur WhatsApp dès que son OR passe en « prêt ». S'il n'a pas accepté WhatsApp, le SMS habituel part à la place.",
    modele: "vehicule_pret",
  },
  {
    cle: "rappel_rdv",
    titre: "Rappel de rendez-vous",
    aide: "Envoie le rappel de la veille sur WhatsApp plutôt que par SMS, à l'heure réglée dans SMS Twilio.",
    modele: "rappel_rdv",
  },
];

const COULEUR_STATUT = {
  APPROVED: "bg-emerald-100 text-emerald-800",
  PENDING: "bg-amber-100 text-amber-800",
  REJECTED: "bg-red-100 text-red-800",
};

export default function ParametresWhatsApp() {
  const [chargement, setChargement] = useState(true);
  const [enreg, setEnreg] = useState(false);
  const [reglages, setReglages] = useState({ mode: "desactive", automatismes: {}, horaires_retrait: "" });
  const [modeles, setModeles] = useState([]);
  const [profil, setProfil] = useState({ about: "", address: "", description: "", email: "", websites: [""] });
  const [profilDispo, setProfilDispo] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/whatsapp/profil");
      setReglages(data.reglages || {});
      setModeles(data.modeles || []);
      setProfilDispo(!!data.disponible);
      const p = data.profil || {};
      setProfil({
        about: p.about || "",
        address: p.address || "",
        description: p.description || "",
        email: p.email || "",
        websites: p.websites && p.websites.length ? p.websites : [""],
        profile_picture_url: p.profile_picture_url || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les réglages WhatsApp");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const basculer = async (cle, valeur) => {
    setReglages((r) => ({ ...r, automatismes: { ...r.automatismes, [cle]: valeur } }));
    try {
      const { data } = await api.post("/whatsapp/automatismes", { [cle]: valeur });
      setReglages((r) => ({ ...r, automatismes: data.automatismes }));
      toast.success(valeur ? "Automatisme activé" : "Automatisme coupé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Changement refusé");
      charger();
    }
  };

  const enregistrerHoraires = async () => {
    try {
      await api.post("/whatsapp/automatismes", { horaires_retrait: reglages.horaires_retrait });
      toast.success("Horaires de retrait enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const enregistrerNumero = async () => {
    try {
      const { data } = await api.post("/whatsapp/numero", {
        phone_number_id: reglages.phone_number_id,
        numero_affiche: reglages.numero_affiche,
      });
      setReglages((r) => ({ ...r, ...data }));
      toast.success("Numéro d'envoi enregistré");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const enregistrerProfil = async () => {
    setEnreg(true);
    try {
      await api.post("/whatsapp/profil", { ...profil, websites: profil.websites });
      toast.success("Profil WhatsApp mis à jour");
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Meta a refusé la mise à jour");
    } finally {
      setEnreg(false);
    }
  };

  const envoyerLogo = async (fichier) => {
    if (!fichier) return;
    if (fichier.size > 5 * 1024 * 1024) { toast.error("Logo trop lourd (5 Mo maximum)"); return; }
    const lecteur = new FileReader();
    lecteur.onload = async () => {
      setEnreg(true);
      try {
        await api.post("/whatsapp/profil", {
          logo_b64: String(lecteur.result).split(",")[1],
          logo_mime: fichier.type || "image/jpeg",
        });
        toast.success("Logo envoyé à WhatsApp");
        charger();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Logo refusé par Meta");
      } finally {
        setEnreg(false);
      }
    };
    lecteur.readAsDataURL(fichier);
  };

  const statutModele = (nom) => (modeles.find((m) => m.nom === nom) || {}).statut || "ABSENT";

  if (chargement) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 p-6" data-testid="whatsapp-params-chargement">
      <Loader2 className="w-4 h-4 animate-spin" /> Chargement des réglages WhatsApp…
    </div>;
  }

  return (
    <div className="space-y-5" data-testid="parametres-whatsapp">
      <div className="rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-semibold">Envois automatiques</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Chaque automatisme s'active séparément. Un client ne reçoit rien tant qu'il n'a pas
              accepté WhatsApp, et un « STOP » de sa part coupe tout définitivement.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700"
                data-testid="whatsapp-mode-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Mode « {reglages.mode} »
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {AUTOMATISMES.map((a) => {
            const statut = statutModele(a.modele);
            return (
              <div key={a.cle} className="flex items-start gap-3 rounded-lg border border-gray-100 p-3">
                <Switch
                  checked={!!reglages.automatismes?.[a.cle]}
                  onCheckedChange={(v) => basculer(a.cle, v)}
                  data-testid={`whatsapp-auto-${a.cle}`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{a.titre}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${COULEUR_STATUT[statut] || "bg-gray-100 text-gray-600"}`}
                          data-testid={`whatsapp-modele-${a.modele}`}>
                      modèle {statut === "APPROVED" ? "approuvé" : statut === "PENDING" ? "en validation" : statut.toLowerCase()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{a.aide}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          <Label className="text-xs">Horaires de retrait annoncés au client</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={reglages.horaires_retrait || ""}
              onChange={(e) => setReglages((r) => ({ ...r, horaires_retrait: e.target.value }))}
              placeholder="du lundi au vendredi de 8h à 12h et de 13h30 à 18h"
              data-testid="whatsapp-horaires-retrait"
            />
            <Button variant="outline" onClick={enregistrerHoraires} data-testid="whatsapp-horaires-save">
              Enregistrer
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4">
        <h3 className="text-base font-semibold">Numéro d'envoi</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Le numéro depuis lequel partent les messages. À changer ici en cas de nouveau numéro Meta.
        </p>
        <div className="grid gap-3 mt-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Numéro affiché</Label>
            <Input value={reglages.numero_affiche || ""}
                   onChange={(e) => setReglages((r) => ({ ...r, numero_affiche: e.target.value }))}
                   placeholder="+41229809393" data-testid="whatsapp-numero-affiche" />
          </div>
          <div>
            <Label className="text-xs">Identifiant Meta du numéro</Label>
            <Input value={reglages.phone_number_id || ""}
                   onChange={(e) => setReglages((r) => ({ ...r, phone_number_id: e.target.value }))}
                   placeholder="hérité du serveur" data-testid="whatsapp-numero-id" />
          </div>
        </div>
        <Button variant="outline" className="mt-3" onClick={enregistrerNumero}
                data-testid="whatsapp-numero-save">Enregistrer le numéro</Button>
      </div>

      <div className="rounded-xl border border-gray-200 p-4">
        <h3 className="text-base font-semibold">Profil affiché dans WhatsApp</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Ce que vos clients voient en ouvrant la fiche du garage dans WhatsApp.
        </p>
        {!profilDispo && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
            Profil non lisible pour l'instant — vérifiez la configuration du numéro.
          </p>
        )}
        <div className="grid gap-3 mt-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Phrase d'accroche (139 caractères max.)</Label>
            <Input value={profil.about} maxLength={139}
                   onChange={(e) => setProfil((p) => ({ ...p, about: e.target.value }))}
                   placeholder="Garage & carrosserie à Meyrin" data-testid="whatsapp-profil-about" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Adresse</Label>
            <Input value={profil.address}
                   onChange={(e) => setProfil((p) => ({ ...p, address: e.target.value }))}
                   placeholder="Rue… , 1217 Meyrin" data-testid="whatsapp-profil-adresse" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Description et horaires</Label>
            <Textarea rows={3} value={profil.description}
                      onChange={(e) => setProfil((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Entretien, carrosserie, pneus. Ouvert du lundi au vendredi de 8h à 12h et de 13h30 à 18h."
                      data-testid="whatsapp-profil-description" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={profil.email} onChange={(e) => setProfil((p) => ({ ...p, email: e.target.value }))}
                   placeholder="meyrin@car-main.ch" data-testid="whatsapp-profil-email" />
          </div>
          <div>
            <Label className="text-xs">Site internet</Label>
            <Input value={profil.websites?.[0] || ""}
                   onChange={(e) => setProfil((p) => ({ ...p, websites: [e.target.value] }))}
                   placeholder="https://www.car-main.ch" data-testid="whatsapp-profil-site" />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 flex-wrap">
          {profil.profile_picture_url && (
            <img src={profil.profile_picture_url} alt="Logo WhatsApp" className="w-12 h-12 rounded-full object-cover border" />
          )}
          <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border cursor-pointer hover:bg-gray-50"
                 data-testid="whatsapp-profil-logo-label">
            <Upload className="w-4 h-4" /> Changer le logo
            <input type="file" accept="image/jpeg,image/png" className="hidden"
                   onChange={(e) => envoyerLogo(e.target.files?.[0])}
                   data-testid="whatsapp-profil-logo" />
          </label>
          <Button onClick={enregistrerProfil} disabled={enreg}
                  className="bg-[#52a675] hover:bg-[#3d8a5c] ml-auto" data-testid="whatsapp-profil-save">
            {enreg ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Enregistrer le profil
          </Button>
        </div>
      </div>
    </div>
  );
}
